import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Steps, Form, Input, Select, DatePicker, TimePicker, Button, Card, Row, Col, Switch, Upload, InputNumber, Checkbox, Tag, Avatar, Divider, Space, Badge, Modal, Alert, List, Descriptions, ConfigProvider, Spin } from 'antd';
import { UserOutlined, CarOutlined, EnvironmentOutlined, FileTextOutlined, CheckCircleOutlined, PlusOutlined, DeleteOutlined, UploadOutlined, TeamOutlined, SafetyOutlined, ExclamationCircleOutlined, EditOutlined } from '@ant-design/icons';
import SideBar from '../../components/SideBar';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import frFR from "antd/locale/fr_FR";
import { recupererLesContrats, recupererLesContratsActifs, recupererLesEntitesDeconstat } from '../../hooks/useHooks';
import { formaterLesContrats, formaterLesEntites, getEnginsFromContrat } from '../../variables/functions';
import axiosInstance from '../../variables/api/axiosInstance';
import { normalizeParametersOptions } from '../../variables/stringFunctions';
import dayjs from 'dayjs';
import axiosMultiPartDataInstance from '../../variables/api/axiosMultiPartDatainstance';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResume } from '../../components/services/ResumeProvider';

const { Option } = Select;
const { TextArea } = Input;

const DeclarationSinistreForm = () => {
    const [form] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [isAssureConducteur, setIsAssureConducteur] = useState(true);
    const [isAdversaireConducteur, setIsAdversaireConducteur] = useState(true);
    const [adversaires, setAdversaires] = useState([]);
    const [victimes, setVictimes] = useState([]);
    const [typeSinistre, setTypeSinistre] = useState('MATERIEL');
    const [selectedGaranties, setSelectedGaranties] = useState([]);
    const [evaluationInitiale, setEvaluationInitiale] = useState(0);
    const [contratLoading, setContratLoading] = useState(false);
    const [contrats, setContrats] = useState([]);
    const [engins, setEngins] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [selectedContratObj, setSelectedContratObj] = useState({});
    const [selectedContrat, setSelectedContrat] = useState(null);
    // Toutes les périodes de garantie (AN + tous les avenants) de la police : la
    // survenance est validée contre l'UNION, pas contre les dates de la seule
    // version active (un avenant de retrait affiche « 01/01/2026 → 31/12/2025 »).
    const [periodesCouverture, setPeriodesCouverture] = useState([]);
    // Contrats valides de la même immatriculation dans d'AUTRES compagnies (indicatif, non bloquant).
    const [autresCompagnies, setAutresCompagnies] = useState([]);
    const [entites, setEntites] = useState([]);
    const [garanties, setGaranties] = useState([]);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { pushState, consumeForPage } = useResume();
    const restoredResume = consumeForPage("declaration-sinistre");

    useEffect(() => {
        if (!restoredResume?.data) return;
        const data = restoredResume.data;
        setCurrentStep(data.currentStep ?? 0);
        setFormData(data.formData || {});
        setIsAssureConducteur(data.isAssureConducteur ?? true);
        setIsAdversaireConducteur(data.isAdversaireConducteur ?? true);
        setAdversaires(data.adversaires || []);
        setVictimes(data.victimes || []);
        setTypeSinistre(data.typeSinistre || 'MATERIEL');
        setSelectedGaranties(data.selectedGaranties || []);
        setSelectedContratObj(data.selectedContratObj || {});
        setSelectedContrat(data.selectedContrat || null);
        setEngins(data.engins || []);
        form.setFieldsValue(data.formData || {});
        // Reprise de brouillon : recharge les périodes de garantie de la police.
        const cid = data.selectedContratObj?.codeId || data.formData?.contratCodeId;
        if (cid) {
            axiosInstance.get(`/api/contrat/periodes-couverture/${cid}`)
                .then((res) => setPeriodesCouverture(Array.isArray(res.data) ? res.data : []))
                .catch(() => setPeriodesCouverture([]));
        }
    }, [restoredResume, form]);


    // Périodes de garantie valides (on écarte les bornes inversées, ex. un avenant
    // de retrait dont l'effet est postérieur à l'échéance du terme).
    const periodesValides = (periodesCouverture || []).filter(
        (p) => p?.dateEffet && p?.dateEcheance
            && !dayjs(p.dateEcheance).startOf('day').isBefore(dayjs(p.dateEffet).startOf('day'))
    );

    // Valide une survenance contre l'UNION des périodes de garantie de la police.
    // Repli tolérant si les périodes ne sont pas encore chargées (le backend
    // revalide de toute façon via resolveContratCouvrant).
    const validerSurvenanceCouverte = (dateTime) => {
        if (!periodesValides.length) return Promise.resolve();
        const d = dateTime.startOf('day');
        const couvert = periodesValides.some((p) =>
            !d.isBefore(dayjs(p.dateEffet).startOf('day'))
            && !d.isAfter(dayjs(p.dateEcheance).startOf('day'))
        );
        if (couvert) return Promise.resolve();
        const liste = periodesValides
            .map((p) => `${dayjs(p.dateEffet).format('DD/MM/YYYY')} → ${dayjs(p.dateEcheance).format('DD/MM/YYYY')}`)
            .join(' ; ');
        return Promise.reject(`Hors période de garantie. Périodes couvertes : ${liste}`);
    };

    const searchContrats = async (searchValue) => {
        setContratLoading(true);
        try {
            recupererLesContratsActifs(searchValue)
                .then((response) => {
                    setContrats(formaterLesContrats(response.data.content));
                    // setContratLoading(false);
                })
                .catch((error) => {
                    console.log(error);
                    setContrats([]);
                    // setContratLoading(false);
                });
        } catch (error) {
            console.error('Erreur recherche contrats:', error);
        } finally {
            setContratLoading(false);
        }
    };


    useEffect(() => {
        searchContrats('');
    }, []);


    useEffect(() => {
        recupererLesEntitesDeconstat()
            .then((response) => {
                // console.log(formaterLesEntites(response.data));
                setEntites(formaterLesEntites(response.data));
            })
            .catch((error) => {
                console.log(error);
                setEntites([]);
            });
    }, []);

    useEffect(() => {
        // On stocke un payload allégé pour éviter les dépassements de taille
        const slimFormData = {
            ...formData,
            photos: undefined,
            croquis: undefined,
        };
        const slimVictimes = (victimes || []).map((v) => ({
            nom: v.nom,
            prenom: v.prenom,
            typeVictime: v.typeVictime,
            blessures: v.blessures,
        }));
        const slimAdversaires = (adversaires || []).map((a) => ({
            nom: a.nom,
            prenom: a.prenom,
            immatriculation: a.immatriculation,
            assurance: a.assurance,
        }));

        pushState({
            path: `${location.pathname}${location.search}`,
            module: "SINISTRES",
            summary: "Déclaration de sinistre en cours",
            payload: {
                page: "declaration-sinistre",
                data: {
                    currentStep,
                    formData: slimFormData,
                    isAssureConducteur,
                    isAdversaireConducteur,
                    adversaires: slimAdversaires,
                    victimes: slimVictimes,
                    typeSinistre,
                    selectedGaranties,
                    selectedContrat,
                    selectedContratObj,
                    engins,
                },
            },
        });
    }, [
        currentStep,
        formData,
        isAssureConducteur,
        isAdversaireConducteur,
        adversaires,
        victimes,
        typeSinistre,
        selectedGaranties,
        selectedContrat,
        selectedContratObj,
        engins,
        location,
        pushState,
    ]);

    const formatGarantieCode = (value) => {
        if (!value) return '';

        // On nettoie la valeur
        value = value.trim();

        // Si la valeur a 3 caractères ou moins, on la retourne telle quelle
        if (value.length <= 3) {
            return value;
        }

        // Sinon, on prend la première lettre de chaque mot
        return value
            .split(/\s+/)          // découpe en mots
            .map(word => word[0].toUpperCase()) // prend la première lettre
            .join('');              // joint les lettres ensemble
    }

    useEffect(() => {
        if (formData?.contratCodeId) {
            axiosInstance
                .get(`/api/contrat/preview/${formData?.contratCodeId}`)
                .then((response) => {
                    console.log("Preview:", response.data);
                    const engine = response?.data?.engins?.find((e) => e.immatriculation === formData?.enginId);
                    let g = [];
                    engine && engine?.compulsoryGuaranties.forEach((ga) => {
                        !ga?.nom.toLowerCase().startsWith("surprime") && g.push({
                            value: ga.nom, label: ga.nom, garantie: ga, code: ga?.nom?.startsWith("RC") ? "RC" : formatGarantieCode(ga?.nom)
                        });
                    });
                    setGaranties(g);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [formData?.contratCodeId]);

    const steps = [
        {
            title: 'Informations sur le conducteur',
            subtitle: 'Ajouter les infos du conducteur',
            icon: 'bx bx-user'
        },
        {
            title: 'Infos sur la partie adverse',
            subtitle: 'Saisir les infos de la partie adverse',
            icon: 'bx bx-user-pin'
        },
        {
            title: 'Informations sur l\'accident',
            subtitle: 'Renseigner les infos sur l\'accident',
            icon: 'bx bxs-car-crash'
        },
        {
            title: 'Informations sur la(les) victime(s)',
            subtitle: 'Renseigner les infos sur les victimes',
            icon: 'bx bx-user-voice'
        },
        {
            title: 'Résumé de la déclaration',
            subtitle: 'Verifier les informations',
            icon: 'bx bx-detail'
        }
    ];

    const next = async () => {
        try {
            const values = await form.validateFields();
            console.log('Form values:', formData);
            console.log('Form values1 : ', selectedGaranties);
            setFormData({ ...formData, ...values });
            setCurrentStep(currentStep + 1);
        } catch (error) {
            console.error('Validation error:', error);
        }
    };

    const prev = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSubmit = () => {
        Modal.confirm({
            title: 'Confirmer la déclaration de sinistre',
            icon: <ExclamationCircleOutlined />,
            content: 'Êtes-vous sûr de vouloir enregistrer cette déclaration de sinistre ?',
            okText: 'Confirmer',
            cancelText: 'Annuler',
            okButtonProps: { type: 'primary', loading: loading },
            onOk: () => {
                console.log('Données complètes:', { ...formData, adversaires, victimes, selectedGaranties });
                saveSinistre();
            }
        });
    };

    const saveSinistre = () => {
        setLoading(true);
        const formDataToSend = new FormData();
        const photos = formData?.photos;
        // Préparer l'objet de données (sans les photos)
        const dataObject = {
            ...formData,
            adversaires,
            victimes,
            garanties: selectedGaranties,
            heureSurvenance: formData?.heureSurvenance ? formData?.heureSurvenance.format('HH:mm') : null,
            photos: null,
            croquis: null
        };

        console.log(dataObject);
        // Convertir l'objet en JSON et l'ajouter au FormData
        const dataBlob = new Blob([JSON.stringify(dataObject)], {
            type: 'application/json'
        });
        formDataToSend.append('data', dataBlob);

        // Ajouter les photos
        if (photos && photos.length > 0) {
            photos.forEach((photo) => {
                formDataToSend.append('photos', photo);
            });
        }

        // Ajouter les croquis
        if (formData?.croquis && formData?.croquis.length > 0) {
            formData?.croquis.forEach((croquis) => {
                formDataToSend.append('croquis', croquis);
            });
        }

        axiosMultiPartDataInstance
            .post('/api/sinistre/web-new', formDataToSend)
            .then((response) => {
                console.log(response);
                if (response.data) {
                    navigate(`/details-sinistre/v2`, { replace: true, state: { codeId: response.data.codeId } });
                    Modal.success({
                        title: 'Déclaration enregistrée',
                        content: 'La déclaration de sinistre a été enregistrée avec succès !',
                    });
                } else {
                    Modal.error({
                        title: 'Erreur',
                        content: 'Une erreur est survenue lors de l\'enregistrement de la déclaration de sinistre.',
                    });
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                if (error?.response?.data?.message) {
                    Modal.error({
                        title: 'Erreur',
                        content: error?.response?.data?.message,
                    });
                } else {
                    Modal.error({
                        title: 'Erreur',
                        content: 'Une erreur est survenue lors de l\'enregistrement de la déclaration de sinistre.',
                    });
                }
                setLoading(false);
            });

    }

    const calculateEvaluation = () => {
        let total = 0;
        const nbBlessesLegers = form.getFieldValue('nbBlessesLegers') || 0;
        const nbBlessesMoyens = form.getFieldValue('nbBlessesMoyens') || 0;
        const nbBlessesGraves = form.getFieldValue('nbBlessesGraves') || 0;
        const tauxResponsabilite = form.getFieldValue('tauxResponsabilite') === 0 ? 0 : form.getFieldValue('tauxResponsabilite') || 100;
        const nbDeces = form.getFieldValue('nbDeces') || 0;
        const typeDegats = form.getFieldValue('typeDegatsMateriels');

        const evalu = {
            nombreBlessesLegers: parseInt(nbBlessesLegers),
            nombreBlessesMoyens: parseInt(nbBlessesMoyens),
            nombreBlessesGraves: parseInt(nbBlessesGraves),
            nombreDeces: parseInt(nbDeces),
            typeDegats: typeDegats === "CORPOREL" ? null : typeDegats,
        };

        axiosInstance
            .post(`/api/bareme-sinistre/evaluation`, evalu)
            .then((res) => {
                console.log("Réponse évaluation:", res.data);
                total = res.data;
                setEvaluationInitiale(total * (tauxResponsabilite / 100));
                form.setFieldsValue({ evaluationInitiale: total });
            })
            .catch((err) => {
                console.error("Erreur évaluation:", err);
            });
    };

    // ---- NOUVEAU : Récupération des sociétés d'assurance via /api/organisme/assurance
    const [assuranceOptions, setAssuranceOptions] = useState([]);
    const [professions, setProfessions] = useState([]);
    useEffect(() => {
        const fetchAssurances = async () => {
            try {
                const res = await axiosInstance.get(`/api/organisme/assurance`);
                const options = (Array.isArray(res.data) ? res.data : []).map((it) => {
                    // On tente de couvrir plusieurs schémas de réponse possibles
                    const label = it?.raisonSociale;
                    const value = label;
                    return { value, label };
                });
                // Tri alpha par label
                options.sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
                setAssuranceOptions(options);
            } catch (e) {
                console.error("Erreur lors de la récupération des assurances:", e);
                setAssuranceOptions([]);
            }
        };
        fetchAssurances();

        axiosInstance
            .get(`/api/parametre/type/PROFESSION`)
            .then((res) => {
                setProfessions(normalizeParametersOptions(res.data));
            })
            .catch((error) => {
                console.error("Erreur lors de la récupération des professions:", error);
            });
    }, []);

    // Étape 1: Assuré & Véhicule
    const RenderStep1 = () => (
        <Card style={{ borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div style={{ background: '#242166', padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                <h2 style={{ color: '#fff', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                    <UserOutlined style={{ marginRight: '12px' }} />
                    Informations sur le Conducteur et le Véhicule
                </h2>
            </div>

            {/* // onChange={(valeur, obj) => {
                            //     setSelectedContratObj(obj.contrat);
                            //     setSelectedContrat(valeur);
                            //     setEngins(getEnginsFromContrat(obj.contrat));
                            // }} */}
            <Row gutter={16} marginTop="0px">
                <Col xs={24} sm={12}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Contrat</span>}
                        name="contratCodeId"
                        rules={[{ required: true, message: 'Sélectionnez un contrat' }]}
                    >
                        <Select
                            placeholder="Rechercher un contrat"
                            showSearch
                            filterOption={false}
                            loading={contratLoading}
                            onSearch={(value) => {
                                setSearchValue(value);
                                searchContrats(value);
                            }}
                            onChange={(valeur, option) => {
                                const obj = option?.contrat || {};
                                setSelectedContrat(valeur);
                                setSelectedContratObj(obj);
                                setEngins(getEnginsFromContrat(obj));
                                // Indicatif : la même immatriculation a-t-elle un contrat VALIDE
                                // dans une AUTRE compagnie ? (ex. moto incorporée dans une flotte)
                                setAutresCompagnies([]);
                                if (obj?.immatriculation) {
                                    const norm = (s) => (s || "").toString().trim().toUpperCase();
                                    axiosInstance
                                        .get(`/api/sinistre/engin-autres-compagnies`, {
                                            params: { immatriculation: obj.immatriculation },
                                        })
                                        .then((res) => {
                                            const list = Array.isArray(res.data) ? res.data : [];
                                            setAutresCompagnies(
                                                list.filter((c) => norm(c.raisonSociale) !== norm(obj.raisonSociale))
                                            );
                                        })
                                        .catch(() => setAutresCompagnies([]));
                                }
                                // Charge toutes les périodes de garantie (avenants) de la police.
                                setPeriodesCouverture([]);
                                const cid = obj?.codeId;
                                if (cid) {
                                    axiosInstance.get(`/api/contrat/periodes-couverture/${cid}`)
                                        .then((res) => {
                                            setPeriodesCouverture(Array.isArray(res.data) ? res.data : []);
                                            form.validateFields(['dateSurvenance', 'heureSurvenance']).catch(() => { });
                                        })
                                        .catch(() => setPeriodesCouverture([]));
                                }
                            }}
                            notFoundContent={contratLoading ? <Spin size="small" /> : 'Aucun contrat trouvé'}
                            options={contrats}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Véhicule sinistré</span>}
                        name="enginId"
                        rules={[{ required: true, message: 'Sélectionnez un véhicule' }]}
                    >
                        <Select options={engins} placeholder="Sélectionner le véhicule" style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>

            {autresCompagnies.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Ce véhicule a un contrat valide dans une autre compagnie"
                    description={
                        <List
                            size="small"
                            dataSource={autresCompagnies}
                            renderItem={(c) => (
                                <List.Item style={{ padding: '4px 0' }}>
                                    <Space wrap size={6}>
                                        <Tag color="volcano">{c.raisonSociale || 'Compagnie inconnue'}</Tag>
                                        <span>Police {c.numeroPolice || '—'}</span>
                                        {c.dateEcheance && (
                                            <span style={{ color: '#8c8c8c' }}>
                                                échéance {dayjs(c.dateEcheance).format('DD/MM/YYYY')}
                                            </span>
                                        )}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    }
                />
            )}

            {/* <Divider style={{ marginTop: "0px" }} /> */}

            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Date de survenanceee</span>}
                        name="dateSurvenance"
                        rules={[
                            { required: true, message: 'Date requise' },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();

                                    const heure = form.getFieldValue('heureSurvenance');

                                    const dateTime = value.clone();
                                    if (heure) {
                                        dateTime.hour(heure.hour()).minute(heure.minute());
                                    }

                                    const now = new Date();

                                    // 1️⃣ Pas dans le futur
                                    if (dateTime.isAfter(now)) {
                                        return Promise.reject('La date et l\'heure ne peuvent pas être dans le futur');
                                    }

                                    // 2️⃣ Union des périodes de garantie (AN + tous les avenants) : un
                                    // sinistre tardif tombant dans un avenant antérieur est accepté.
                                    return validerSurvenanceCouverte(dateTime);
                                }
                            }
                        ]}
                    >
                        <DatePicker
                            format="DD/MM/YYYY"
                            disabledDate={(current) => current && current > new Date()}
                            style={{ width: '100%' }}
                            placeholder="Sélectionner la date"
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Heure de survenance</span>}
                        name="heureSurvenance"
                        rules={[
                            { required: true, message: 'Heure requise' },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();

                                    const date = form.getFieldValue('dateSurvenance');

                                    if (!date) return Promise.resolve();

                                    const dateTime = date.clone().hour(value.hour()).minute(value.minute());
                                    const now = new Date();

                                    // 1️⃣ Pas dans le futur
                                    if (dateTime.isAfter(now)) {
                                        return Promise.reject('La date et l\'heure ne peuvent pas être dans le futur');
                                    }

                                    // 2️⃣ Union des périodes de garantie (AN + tous les avenants).
                                    return validerSurvenanceCouverte(dateTime);

                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <TimePicker
                            style={{ width: '100%' }}
                            format="HH:mm"
                            placeholder="Sélectionner l'heure"
                        />
                    </Form.Item>
                </Col>
            </Row>


            <Form.Item label={<span style={{ fontWeight: '600', fontSize: '15px' }}>L'assuré était-il le conducteur ?</span>}>
                <Switch
                    checked={isAssureConducteur}
                    onChange={setIsAssureConducteur}
                    checkedChildren="Oui"
                    unCheckedChildren="Non"
                    style={{ background: isAssureConducteur ? '#52c41a' : '#d9d9d9' }}
                />
            </Form.Item>

            <div style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', padding: '16px', borderRadius: '6px', marginTop: '16px' }}>
                <h4 style={{ color: '#1565c0', marginBottom: '16px' }}>
                    <CarOutlined style={{ marginRight: '8px' }} />
                    Informations sur le {isAssureConducteur ? 'permis de l\'assuré' : 'conducteur'}
                </h4>

                {!isAssureConducteur && (
                    <>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label={<span style={{ fontWeight: '600' }}>Nom du conducteur</span>}
                                    name={['conducteur', 'nom']}
                                    rules={[{ required: true, message: 'Nom requis' }]}
                                >
                                    <Input placeholder="Nom" style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label={<span style={{ fontWeight: '600' }}>Prénom du conducteur</span>}
                                    name={['conducteur', 'prenom']}
                                    rules={[{ required: true, message: 'Prénom requis' }]}
                                >
                                    <Input placeholder="Prénom" style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label={<span style={{ fontWeight: '600' }}>Téléphone</span>}
                                    name={['conducteur', 'telephone']}
                                >
                                    <Input placeholder="Téléphone" style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label={<span style={{ fontWeight: '600' }}>Adresse</span>}
                                    name={['conducteur', 'adresse']}
                                >
                                    <Input placeholder="Adresse" style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={<span style={{ fontWeight: '600' }}>Numéro de permis</span>}
                            name={['permis', 'numero']}
                        >
                            <Input placeholder="Numéro de permis" style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={<span style={{ fontWeight: '600' }}>Date d'obtention</span>}
                            name={['permis', 'dateObtention']}
                        >
                            <DatePicker format="DD/MM/YYYY" disabledDate={(current) => current && current > new Date()} style={{ width: '100%' }} placeholder="Date d'obtention" />
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </Card>
    );

    // Étape 2: Adversaires
    const RenderStep2 = () => {
        const [showAdversaireModal, setShowAdversaireModal] = useState(false);
        const [adversaireForm] = Form.useForm();
        const [typeAdversaire, setTypeAdversaire] = useState('VEHICULE');
        const [typeSerie, setTypeSerie] = useState('TG');

        const ajouterAdversaire = async () => {
            try {
                const values = await adversaireForm.validateFields();
                // Construire l'immatriculation complète pour l'affichage
                let immatriculationComplete = '';
                if (typeAdversaire === 'VEHICULE') {
                    const { typeSerie, matricule, numeroSerie } = values;
                    if (typeSerie === 'TG' && numeroSerie) {
                        immatriculationComplete = `${typeSerie}-${matricule}-${numeroSerie}`;
                    } else {
                        immatriculationComplete = `${matricule}`;
                    }
                }

                setAdversaires([...adversaires, { ...values, id: Date.now(), typeAdversaire, immatriculation: immatriculationComplete }]);
                adversaireForm.resetFields();
                setShowAdversaireModal(false);
            } catch (error) {
                console.error('Error:', error);
            }
        };

        const supprimerAdversaire = (id) => {
            setAdversaires(adversaires.filter(a => a.id !== id));
        };

        return (
            <>
                <Card style={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <div style={{ background: '#242166', padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <h2 style={{ color: '#fff', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                                    <TeamOutlined style={{ marginRight: '12px' }} />
                                    Adversaires impliqués
                                </h2>
                            </Col>
                            <Col>
                                <Badge count={adversaires.length} style={{ backgroundColor: '#52c41a' }}>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setShowAdversaireModal(true)}
                                        style={{ background: '#fff', color: '#242166', border: 'none' }}
                                    >
                                        Ajouter un adversaire
                                    </Button>
                                </Badge>
                            </Col>
                        </Row>
                    </div>

                    {adversaires.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <TeamOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                            <p style={{ color: '#999', marginTop: '16px' }}>Aucun adversaire ajouté</p>
                            <Button style={{ background: '#242166' }} type="primary" icon={<PlusOutlined />} onClick={() => setShowAdversaireModal(true)}>
                                Ajouter le premier adversaire
                            </Button>
                        </div>
                    ) : (
                        <Row gutter={[16, 16]} align="stretch">
                            {adversaires.map((adv) => (
                                <Col xs={24} sm={12} md={8} key={adv.id}>
                                    <Card
                                        size="small"
                                        style={{
                                            height: "100%", // makes the card fill the column height
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between", // ensures actions stay at bottom
                                            borderLeft: "4px solid #f5576c",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                                        }}
                                        actions={[
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => supprimerAdversaire(adv.id)}
                                            >
                                                Retirer
                                            </Button>
                                        ]}
                                    >
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                                                <Avatar
                                                    icon={
                                                        adv.typeAdversaire === "VEHICULE" ? <CarOutlined /> : <UserOutlined />
                                                    }
                                                    style={{
                                                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                                    }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: "bold" }}>
                                                        {adv.typeAdversaire === "AUTRE" ? `${adv.description} - ` : ""}
                                                        {adv.nom} {adv.prenom}
                                                    </div>
                                                    <Tag
                                                        color={
                                                            adv.typeAdversaire === "VEHICULE"
                                                                ? "blue"
                                                                : adv.typeAdversaire === "PIETON"
                                                                    ? "green"
                                                                    : "orange"
                                                        }
                                                    >
                                                        {adv.typeAdversaire}
                                                    </Tag>
                                                </div>
                                            </div>
                                            {adv.immatriculation && (
                                                <div style={{ fontSize: "13px", color: "#666" }}>
                                                    <CarOutlined /> {adv.immatriculation}
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                    )}
                </Card>

                <Modal
                    title="Ajouter un Adversaire"
                    open={showAdversaireModal}
                    onCancel={() => setShowAdversaireModal(false)}
                    onOk={ajouterAdversaire}
                    width={1200}
                    okText="Ajouter"
                    cancelText="Annuler"
                    zIndex={2000}
                    closable={false}
                    maskClosable={false}
                >
                    <Form form={adversaireForm} layout="vertical">
                        <Form.Item label="Type d'adversaire" name="typeAdversaire" initialValue="VEHICULE">
                            <Select style={{ width: "100%" }} onChange={setTypeAdversaire}>
                                <Option value="PIETON">🚶 Piéton</Option>
                                <Option value="VEHICULE">🚗 Véhiculé</Option>
                                <Option value="AUTRE">⚠️ Autre (Objet, Mur, etc.)</Option>
                            </Select>
                        </Form.Item>

                        {typeAdversaire === 'PIETON' && (
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Nom" name="nom" rules={[{ required: true }]}>
                                        <Input style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Prénom" name="prenom" rules={[{ required: true }]}>
                                        <Input style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Téléphone" name="telephone">
                                        <Input style={{ width: "100%" }} placeholder='Téléphone' />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Adresse" name="adresse">
                                        <Input style={{ width: "100%" }} placeholder='Adresse' />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        {typeAdversaire === 'VEHICULE' && (
                            <Row gutter={16}>

                            </Row>
                        )}

                        {typeAdversaire === 'AUTRE' && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="Nom du propriétaire" name="nom">
                                        <Input style={{ width: "100%" }} placeholder="Nom complet ou Dénomination sociale" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item label="Description" name="description" rules={[{ required: true }]}>
                                        <Input style={{ width: "100%" }} placeholder="Ex: Mur, Poteau, Animal, etc." />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        <Row gutter={16}>

                        </Row>

                        {typeAdversaire === 'VEHICULE' && (
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item label="Nom" name="nom">
                                        <Input style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label="Prénom" name="prenom">
                                        <Input style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label="Téléphone" name="telephone">
                                        <Input style={{ width: "100%" }} placeholder='Téléphone' />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label="Adresse" name="adresse">
                                        <Input style={{ width: "100%" }} placeholder='Adresse' />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label="Immatriculation" name="immatriculation">
                                        <Input.Group compact style={{ width: "100%" }}>
                                            <Form.Item
                                                name="typeSerie"
                                                noStyle
                                                initialValue="TG"
                                            >
                                                <Select
                                                    style={{ width: '25%' }}
                                                    onChange={(value) => {
                                                        setTypeSerie(value);
                                                        if (value !== 'TG') {
                                                            adversaireForm.setFieldsValue({ numeroSerie: undefined });
                                                        }
                                                    }}
                                                >
                                                    <Option value="TG">TG</Option>
                                                    <Option value="AUTRE">Autre</Option>
                                                </Select>
                                            </Form.Item>
                                            <Form.Item
                                                name="matricule"
                                                noStyle
                                                rules={[{ required: true, message: 'Immatriculation requise' }]}
                                            >
                                                <Input
                                                    style={{ width: typeSerie === 'TG' ? '45%' : '75%' }}
                                                    placeholder="Ex: 1234 AB"
                                                />
                                            </Form.Item>
                                            {typeSerie === 'TG' && (
                                                <Form.Item
                                                    name="numeroSerie"
                                                    noStyle
                                                    rules={[{ required: true, message: 'N° série requis' }]}
                                                >
                                                    <Input
                                                        style={{ width: '30%' }}
                                                        placeholder="N° série"
                                                    />
                                                </Form.Item>
                                            )}
                                        </Input.Group>
                                    </Form.Item>

                                </Col>
                                <Col span={6}>
                                    <Form.Item label="Marque" name="marque">
                                        <Input style={{ width: "100%" }} placeholder='Marque' />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label="Nbre de pers. transportées" name="nombrePersonnesTransportees">
                                        <Input style={{ width: "100%" }} placeholder='Nbre de pers. transportées' />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label="Vitesse de l'engin" name="vitesse">
                                        <Input style={{ width: "100%" }} placeholder="Vitesse de l'engin" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="L'engin venant de" name="provenance">
                                        <Input style={{ width: "100%" }} placeholder="L'engin venant de" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="L'engin se dirigeait vers" name="destination">
                                        <Input style={{ width: "100%" }} placeholder="L'engin se dirigeait vers" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Type de dommages" name="typeSinistre">
                                        <Select style={{ width: "100%" }}>
                                            <Option value="MATERIEL">🔧 Matériel</Option>
                                            <Option value="CORPOREL">🏥 Corporel</Option>
                                            <Option value="MIXTE">⚠️ Mixte</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Société d'assurance de la partie adverse" name="societeAssurance">
                                        <Select style={{ width: "100%" }} options={assuranceOptions} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                        {typeAdversaire === 'VEHICULE' && (<>
                            <Form.Item label={<span style={{ fontWeight: '600', fontSize: '15px' }}>L'assuré était-il le conducteur ?</span>}>
                                <Switch
                                    checked={isAdversaireConducteur}
                                    onChange={(checked, e) => {
                                        e.stopPropagation(); // ⛔ bloque la propagation du clic
                                        setIsAdversaireConducteur(checked);
                                    }}
                                    checkedChildren="Oui"
                                    unCheckedChildren="Non"
                                    style={{ background: isAdversaireConducteur ? '#52c41a' : '#d9d9d9' }}
                                />
                            </Form.Item>

                            <div style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', padding: '16px', borderRadius: '6px', marginTop: '16px' }}>
                                <h4 style={{ color: '#1565c0', marginBottom: '16px' }}>
                                    <CarOutlined style={{ marginRight: '8px' }} />
                                    Informations sur le {isAdversaireConducteur ? 'permis de la partie adverse' : 'conducteur'}
                                </h4>

                                {!isAdversaireConducteur && (
                                    <>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}>
                                                <Form.Item
                                                    label={<span style={{ fontWeight: '600' }}>Nom du conducteur</span>}
                                                    name={['conducteur', 'nom']}
                                                    rules={[{ required: true, message: 'Nom requis' }]}
                                                >
                                                    <Input placeholder="Nom" style={{ width: "100%" }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Form.Item
                                                    label={<span style={{ fontWeight: '600' }}>Prénom du conducteur</span>}
                                                    name={['conducteur', 'prenom']}
                                                    rules={[{ required: true, message: 'Prénom requis' }]}
                                                >
                                                    <Input placeholder="Prénom" style={{ width: "100%" }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}>
                                                <Form.Item
                                                    label={<span style={{ fontWeight: '600' }}>Téléphone</span>}
                                                    name={['conducteur', 'telephone']}
                                                >
                                                    <Input placeholder="Téléphone" style={{ width: "100%" }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Form.Item
                                                    label={<span style={{ fontWeight: '600' }}>Adresse</span>}
                                                    name={['conducteur', 'adresse']}
                                                >
                                                    <Input placeholder="Adresse" style={{ width: "100%" }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                )}

                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label={<span style={{ fontWeight: '600' }}>Numéro de permis</span>}
                                            name={['permis', 'numero']}
                                        >
                                            <Input placeholder="Numéro de permis" style={{ width: "100%" }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label={<span style={{ fontWeight: '600' }}>Date d'obtention</span>}
                                            name={['permis', 'dateObtention']}
                                        >
                                            <DatePicker format="DD/MM/YYYY" disabledDate={(current) => current && current > new Date()} style={{ width: '100%' }} placeholder="Date d'obtention" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        </>)}
                        <Form.Item label="Description des dégâts" name="descriptionDesDegats">
                            <TextArea
                                rows={4}
                                placeholder="Décrivez précisément les dommages constatés…"
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </>
        );
    };

    // Étape 3: Détails de l'accident
    const RenderStep3 = () => (
        <Card style={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div style={{ background: '#242166', padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                <h2 style={{ color: '#fff', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                    <EnvironmentOutlined style={{ marginRight: '12px' }} />
                    Détails de l'Accident
                </h2>
            </div>

            <div style={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', padding: '16px', borderRadius: '12px', marginBottom: '24px' }}>
                <h4 style={{ color: '#e65100', marginBottom: '16px' }}>📍 Localisation et Trajet</h4>
                <Row gutter={16}>
                    <Col xs={24}>
                        <Form.Item
                            label={<span style={{ fontWeight: '600' }}>Lieu du sinistre</span>}
                            name="lieu"
                            rules={[{ required: true, message: 'Lieu requis' }]}
                        >
                            <Input style={{ width: "100%" }} placeholder="Adresse exacte du sinistre" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Provenance</span>} name="provenance">
                            <Input style={{ width: "100%" }} placeholder="Lieu de départ" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Destination</span>} name="destination">
                            <Input style={{ width: "100%" }} placeholder="Lieu d'arrivée" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Vitesse estimée (km/h)</span>} name="vitesse">
                            <InputNumber min={0} max={200} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Entité ayant fait le constat</span>} name="entiteConstat"
                            rules={[{ required: true, message: "L'entité de constat est obligatoire." }]}>
                            <Select
                                showSearch
                                allowClear
                                style={{ width: "100%" }}
                                placeholder="Cherchez et sélectionnez"
                                options={entites}
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? "").toLowerCase().includes((input ?? "").toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            <div style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', padding: '16px', borderRadius: '12px', marginBottom: '24px' }}>
                <h4 style={{ color: '#2e7d32', marginBottom: '16px' }}>👤 Témoin (Optionnel)</h4>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Nom du témoin</span>} name={['temoin', 'nom']}>
                            <Input style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Prénom du témoin</span>} name={['temoin', 'prenom']}>
                            <Input style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Téléphone</span>} name={['temoin', 'telephone']}>
                            <Input style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={<span style={{ fontWeight: '600' }}>Adresse</span>} name={['temoin', 'adresse']}>
                            <Input style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            <Divider />

            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Type de dommage sur l'assuré</span>}
                        name="typeDommageAssure"
                        rules={[{ required: true }]}
                    >
                        <Select style={{ width: "100%" }} placeholder="Sélectionner">
                            <Option value="AUCUN">Aucun dommage</Option>
                            <Option value="MATERIEL">Matériel uniquement</Option>
                            <Option value="CORPOREL">Corporel uniquement</Option>
                            <Option value="MIXTE">Matériel et Corporel</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Type de dommage général</span>}
                        name="typeSinistreGeneral"
                        rules={[{ required: true }]}
                    >
                        <Select style={{ width: "100%" }} placeholder="Sélectionner" onChange={setTypeSinistre}>
                            <Option value="MATERIEL">🔧 Matériel</Option>
                            <Option value="CORPOREL">🏥 Corporel</Option>
                            <Option value="MIXTE">⚠️ Mixte</Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            {(typeSinistre === 'MATERIEL' || typeSinistre === 'MIXTE') && (
                <Form.Item
                    label={<span style={{ fontWeight: '600' }}>Description des dégâts matériels</span>}
                    name="descriptionDegatsMateriels"
                    rules={[{ required: true }]}
                >
                    <TextArea rows={4} placeholder="Décrire les dégâts matériels..." style={{ width: "100%" }} />
                </Form.Item>
            )}

            {(typeSinistre === 'CORPOREL' || typeSinistre === 'MIXTE') && (
                <Form.Item
                    label={<span style={{ fontWeight: '600' }}>Description des dégâts corporels</span>}
                    name="descriptionDegatsCorporels"
                    rules={[{ required: true }]}
                >
                    <TextArea rows={4} placeholder="Décrire les dégâts corporels..." style={{ width: "100%" }} />
                </Form.Item>
            )}

            <Row gutter={16}>
                <Col xs={24}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Circonstances de l'accident</span>}
                        name="circonstances"
                        rules={[{ required: true }]}
                    >
                        <TextArea rows={4} placeholder="Décrire comment l'accident s'est produit..." style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24}>
                    <Form.Item
                        label={<span style={{ fontWeight: '600' }}>Observation de l'agent</span>}
                        name="observationEngin"
                    >
                        <TextArea rows={3} placeholder="État du véhicule, défauts constatés..." style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item label={<span style={{ fontWeight: '600' }}>📋 Croquis de l'accident</span>} name="croquis">
                        <Upload listType="picture-card" maxCount={1}>
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Croquis</div>
                            </div>
                        </Upload>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item label={<span style={{ fontWeight: '600' }}>📸 Photos de l'accident</span>} name="photos">
                        <Upload listType="picture-card" multiple maxCount={10}>
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Photos</div>
                            </div>
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>
        </Card>
    );

    // Étape 4: Évaluation
    const RenderStep4 = () => {
        const [showVictimeModal, setShowVictimeModal] = useState(false);
        const [victimeForm] = Form.useForm();

        const ajouterVictime = async () => {
            try {
                const values = await victimeForm.validateFields();
                setVictimes([...victimes, { ...values, id: Date.now() }]);
                victimeForm.resetFields();
                setShowVictimeModal(false);
            } catch (error) {
                console.error('Error:', error);
            }
        };

        return (
            <>
                <Card style={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <div style={{ background: '#242166', padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                        <h2 style={{ color: '#fff', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                            <SafetyOutlined style={{ marginRight: '12px' }} />
                            Victimes & Evaluation
                        </h2>
                    </div>

                    <div style={{ background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)', padding: '16px', borderRadius: '12px', marginBottom: '24px' }}>
                        <h4 style={{ color: '#01579b', marginBottom: '16px' }}>🛡️ Garanties affectées</h4>
                        <Form.Item
                            label={<span style={{ fontWeight: '600' }}>Sélectionner les garanties affectées</span>}
                            name="garanties"
                        >
                            <Checkbox.Group style={{ width: '100%' }} onChange={setSelectedGaranties}>
                                <Row gutter={[16, 16]}>
                                    {garanties?.map(g => (
                                        <Col xs={24} sm={12} key={g.value}>
                                            <Checkbox value={g.value} style={{ fontSize: '15px' }}>
                                                <Tag color="blue" style={{ marginRight: '8px' }}>{g.code}</Tag>
                                                {g.label.startsWith("RC") ? "Responsabilité Civile" : g.label}
                                            </Checkbox>
                                        </Col>
                                    ))}
                                </Row>
                            </Checkbox.Group>
                        </Form.Item>
                    </div>

                    {(typeSinistre === 'CORPOREL' || typeSinistre === 'MIXTE') && (
                        <div style={{ background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', padding: '16px', borderRadius: '12px', marginBottom: '24px' }}>
                            <h4 style={{ color: '#c62828', marginBottom: '16px' }}>🏥 Bilan Corporel</h4>
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label={<span style={{ fontWeight: '600' }}>Blessés légers</span>} name="nbBlessesLegers">
                                        <InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label={<span style={{ fontWeight: '600' }}>Blessés moyens</span>} name="nbBlessesMoyens">
                                        <InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label={<span style={{ fontWeight: '600' }}>Blessés graves</span>} name="nbBlessesGraves">
                                        <InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label={<span style={{ fontWeight: '600' }}>Décès</span>} name="nbDeces">
                                        <InputNumber min={0} style={{ width: '100%' }} onChange={calculateEvaluation} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {(typeSinistre === 'MATERIEL' || typeSinistre === 'MIXTE') && (
                        <div style={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', padding: '16px', borderRadius: '12px', marginBottom: '24px' }}>
                            <h4 style={{ color: '#e65100', marginBottom: '16px' }}>🔧 Dégâts Matériels</h4>
                            <Form.Item
                                label={<span style={{ fontWeight: '600' }}>Type de dégâts matériels</span>}
                                name="typeDegatsMateriels"
                            >
                                <Select style={{ width: "100%" }} placeholder="Sélectionner" onChange={calculateEvaluation}>
                                    <Option value="LEGERS">Dégâts légers / Sans précision</Option>
                                    <Option value="MOYENS">Dégâts moyens</Option>
                                    <Option value="IMPORTANTS">Dégâts importants</Option>
                                    <Option value="EXTREMES">Cas extrêmes</Option>
                                </Select>
                            </Form.Item>
                        </div>
                    )}

                    <div style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                        <Row gutter={16} align="middle">
                            <Col xs={24} sm={12}>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#2e7d32', marginBottom: '8px' }}>
                                    💰 Évaluation Initiale
                                </div>
                                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1b5e20' }}>
                                    {evaluationInitiale.toLocaleString()} FCFA
                                </div>
                                <Button type="link" onClick={calculateEvaluation} style={{ padding: 0, marginTop: '8px' }}>
                                    Recalculer automatiquement
                                </Button>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label={<span style={{ fontWeight: '600' }}>Ajuster l'évaluation</span>}
                                    name="evaluationInitiale"
                                >
                                    <InputNumber
                                        min={0}
                                        style={{ width: '100%' }}
                                        value={evaluationInitiale}
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                        parser={value => value.replace(/\s?/g, '')}
                                        addonAfter="FCFA"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Divider />

                    <div style={{ marginBottom: '16px' }}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                    👥 Victimes
                                </h4>
                                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0 0 0' }}>
                                    Les adversaires sont déjà comptés comme victimes
                                </p>
                            </Col>
                            <Col>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowVictimeModal(true)}>
                                    Ajouter une victime
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    {victimes.length > 0 && (
                        <List
                            grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
                            dataSource={victimes}
                            renderItem={(victime) => (
                                <List.Item>
                                    <Card
                                        size="small"
                                        style={{ borderLeft: '4px solid #52c41a' }}
                                        actions={[
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => setVictimes(victimes.filter(v => v.id !== victime.id))}
                                            >
                                                Retirer
                                            </Button>
                                        ]}
                                    >
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                            {victime.nom} {victime.prenom}
                                        </div>
                                        {victime.profession && (
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                💼 {victime.profession}
                                            </div>
                                        )}
                                        {victime.telephone && (
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                📞 {victime.telephone}
                                            </div>
                                        )}
                                    </Card>
                                </List.Item>
                            )}
                        />
                    )}
                </Card>

                <Modal
                    title="Ajouter une Victime"
                    open={showVictimeModal}
                    onCancel={() => setShowVictimeModal(false)}
                    onOk={ajouterVictime}
                    width={700}
                    okText="Ajouter"
                    cancelText="Annuler"
                >
                    <Form form={victimeForm} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Nom" name="nom" rules={[{ required: true }]}>
                                    <Input style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Prénom" name="prenom" rules={[{ required: true }]}>
                                    <Input style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Téléphone" name="telephone">
                                    <Input style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Profession" name="profession">
                                    <Select options={professions} style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item label="Adresse" name="adresse">
                            <Input style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item label="Description des blessures/dommages" name="description">
                            <TextArea rows={3} />
                        </Form.Item>
                    </Form>
                </Modal>
            </>
        );
    };

    // Étape 5: Récapitulatif
    const RenderStep5 = () => (
        <Card style={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div style={{ background: '#242166', padding: '10px 30px', margin: '-24px -24px 24px -24px', borderRadius: '6px 6px 0 0' }}>
                <h2 style={{ color: '#fff', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                    <CheckCircleOutlined style={{ marginRight: '12px' }} />
                    Récapitulatif de la Déclaration
                </h2>
            </div>

            <Alert
                message="Vérification finale"
                description="Veuillez vérifier attentivement toutes les informations avant de soumettre la déclaration."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
            />

            <Descriptions
                title={<span style={{ fontSize: '16px', fontWeight: 'bold', color: '#667eea' }}>👤 Informations sur le Conducteur</span>}
                bordered
                column={{ xs: 1, sm: 2 }}
                style={{ marginBottom: '24px' }}
            >
                <Descriptions.Item label="Contrat">
                    {formData.contratCodeId || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Véhicule">
                    {formData.enginId || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Date de survenance">
                    {formData.dateSurvenance?.format('DD/MM/YYYY') || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Heure">
                    {formData.heureSurvenance?.format('HH:mm') || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Conducteur" span={2}>
                    {isAssureConducteur ? 'Assuré' : `${formData.conducteur?.prenom || ''} ${formData.conducteur?.nom || ''}`}
                </Descriptions.Item>
            </Descriptions>

            {adversaires.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#f5576c', marginBottom: '12px' }}>
                        👥 Adversaires ({adversaires.length})
                    </h4>
                    <Row gutter={[16, 16]}>
                        {adversaires.map((adv, index) => (
                            <Col xs={24} sm={12} md={8} key={adv.id}>
                                <Card size="small" style={{ borderLeft: '3px solid #f5576c' }}>
                                    <div style={{ fontWeight: 'bold' }}>
                                        {index + 1}. {adv.nom} {adv.prenom}
                                    </div>
                                    <Tag color={adv.typeAdversaire === 'VEHICULE' ? 'blue' : 'green'} style={{ marginTop: '4px' }}>
                                        {adv.typeAdversaire}
                                    </Tag>
                                    {adv.immatriculation && <div style={{ fontSize: '12px', marginTop: '4px' }}>{adv.immatriculation}</div>}
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            )}

            <Descriptions
                title={<span style={{ fontSize: '16px', fontWeight: 'bold', color: '#00f2fe' }}>📍 Détails de l'Accident</span>}
                bordered
                column={{ xs: 1, sm: 2 }}
                style={{ marginBottom: '24px' }}
            >
                <Descriptions.Item label="Lieu du sinistre" span={2}>
                    {formData.lieu || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Provenance">
                    {formData.provenance || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Destination">
                    {formData.destination || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Vitesse">
                    {formData.vitesse ? `${formData.vitesse} km/h` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Type de sinistre">
                    <Tag color="orange">{formData.typeSinistreGeneral || typeSinistre}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Circonstances" span={2}>
                    {formData.circonstances || '-'}
                </Descriptions.Item>
            </Descriptions>

            <div style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '12px' }}>
                    💰 Évaluation Financière
                </h4>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1b5e20' }}>
                    {(formData.evaluationInitiale || evaluationInitiale).toLocaleString()} FCFA
                </div>
                {selectedGaranties.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                        <span style={{ fontWeight: '600', marginRight: '8px' }}>Garanties affectées:</span>
                        {garanties
                            .filter(g => selectedGaranties.includes(g.id))
                            .map(g => (
                                <Tag key={g.id} color="blue" style={{ marginTop: '4px' }}>
                                    {g.nom}
                                </Tag>
                            ))}
                    </div>
                )}
            </div>

            {victimes.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a', marginBottom: '12px' }}>
                        👥 Victimes ({victimes.length})
                    </h4>
                    <List
                        grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
                        dataSource={victimes}
                        renderItem={(victime, index) => (
                            <List.Item>
                                <Card size="small" style={{ borderLeft: '3px solid #52c41a' }}>
                                    <div style={{ fontWeight: 'bold' }}>
                                        {index + 1}. {victime.nom} {victime.prenom}
                                    </div>
                                    {victime.profession && <div style={{ fontSize: '12px', color: '#666' }}>💼 {victime.profession}</div>}
                                </Card>
                            </List.Item>
                        )}
                    />
                </div>
            )}
        </Card>
    );

    return (
        <div className="layout-wrapper layout-content-navbar  ">
            <div className="layout-container">
                <SideBar />

                {/* <!-- Layout container --> */}
                <div className="layout-page">
                    <NavBar />

                    {/* <!-- Content wrapper --> */}
                    <div className="content-wrapper">
                        {/* <!-- Content --> */}
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {/* Your main content here */}

                            <div style={{ padding: '24px', minHeight: '100vh' }}>
                                <link href='https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css' rel='stylesheet' />
                                <style>
                                    {`
                                    .bs-stepper-header {
                                        display: flex;
                                        flex-direction: column;
                                        gap: 0;
                                    }
                                    .step {
                                        display: flex;
                                        flex-direction: column;
                                        align-items: stretch;
                                    }
                                    .step-trigger {
                                        display: flex;
                                        align-items: center;
                                        gap: 4px;
                                        background: none;
                                        border: none;
                                        padding: 3px;
                                        padding-left: 12px;
                                        width: 100%;
                                        text-align: left;
                                        cursor: pointer;
                                        border-radius: 8px;
                                        transition: all 0.3s ease;
                                        position: relative;
                                    }
                                    .step-trigger:hover {
                                        background: #f8f9fa;
                                    }
                                    .step.active .step-trigger {
                                        background: #242166;
                                    }
                                    .step.active .step-trigger::before {
                                        content: '';
                                        position: absolute;
                                        left: 0;
                                        top: 0;
                                        bottom: 0;
                                        width: 4px;
                                        background: #fff;
                                        border-radius: 4px;
                                    }
                                    .step.active .bs-stepper-circle {
                                        background: #fff;
                                        color: #667eea;
                                    }
                                    .step.active .bs-stepper-title {
                                        color: #fff;
                                        font-weight: 700;
                                    }
                                    .step.active .bs-stepper-subtitle {
                                        color: rgba(255, 255, 255, 0.9);
                                    }
                                    .bs-stepper-circle {
                                        width: 36px;
                                        height: 36px;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        border-radius: 50%;
                                        background: #e9ecef;
                                        color: #6c757d;
                                        font-size: 18px;
                                        transition: all 0.3s ease;
                                        flex-shrink: 0;
                                    }
                                    .bs-stepper-label {
                                        display: flex;
                                        flex-direction: column;
                                        gap: 1px;
                                        flex: 1;
                                    }
                                    .bs-stepper-title {
                                        font-size: 14px;
                                        font-weight: 600;
                                        color: #333;
                                        transition: all 0.3s ease;
                                        line-height: 1.3;
                                    }
                                    .bs-stepper-subtitle {
                                        font-size: 11px;
                                        color: #6c757d;
                                        transition: all 0.3s ease;
                                        line-height: 1.3;
                                    }
                                    .line {
                                        width: 2px;
                                        height: 12px;
                                        background: #e9ecef;
                                        margin-left: 29px;
                                    }
                                    .card {
                                        background: white;
                                        border-radius: 8px;
                                        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                                        padding: 24px;
                                    }
                                    .btn {
                                        padding: 10px 24px;
                                        border-radius: 6px;
                                        border: none;
                                        font-size: 16px;
                                        cursor: pointer;
                                        transition: all 0.3s ease;
                                        min-width: 120px;
                                    }
                                    .btn-secondary {
                                        background: #e9ecef;
                                        color: #333;
                                    }
                                    .btn-secondary:hover {
                                        background: #d6d9dc;
                                    }
                                    .btn-primary {
                                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                        color: white;
                                    }
                                    .btn-primary:hover {
                                        opacity: 0.9;
                                    }
                                    `}
                                </style>
                                <ConfigProvider locale={frFR}>
                                    <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '24px' }}>
                                        {/* <div className='bs-stepper vertical wizard-modern wizard-modern-vertical wizard-modern-vertical-icons-example mt-2'> */}
                                        {/* Sidebar avec Steps vertical */}
                                        <div style={{ width: '280px', flexShrink: 0 }}>
                                            <div className="card" style={{ position: 'sticky', top: '24px', padding: '16px' }}>
                                                <div className="bs-stepper-header">
                                                    {steps.map((step, index) => (
                                                        <React.Fragment key={index}>
                                                            <div className={currentStep === index ? "step active" : "step"}>
                                                                <button
                                                                    type="button"
                                                                    className="step-trigger"
                                                                    onClick={() => setCurrentStep(index)}
                                                                >
                                                                    <span className="bs-stepper-circle">
                                                                        <i className={step.icon}></i>
                                                                    </span>
                                                                    <span className="bs-stepper-label">
                                                                        <span className="bs-stepper-title">
                                                                            {step.title}
                                                                        </span>
                                                                        <span className="bs-stepper-subtitle">
                                                                            {step.subtitle}
                                                                        </span>
                                                                    </span>
                                                                </button>
                                                            </div>
                                                            {index < steps.length - 1 && <div className="line"></div>}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contenu principal */}
                                        <Col xs={24} md={18}>
                                            <Form form={form} layout="vertical">
                                                {currentStep === 0 && <RenderStep1 />}
                                                {currentStep === 1 && <RenderStep2 />}
                                                {currentStep === 2 && <RenderStep3 />}
                                                {currentStep === 3 && <RenderStep4 />}
                                                {currentStep === 4 && <RenderStep5 />}
                                            </Form>

                                            <Card
                                                style={{
                                                    borderRadius: '4px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                    marginTop: '-10px',
                                                    background: '#fff'
                                                }}
                                            >
                                                <div style={{ textAlign: 'right' }}>
                                                    <Space size="large">
                                                        {currentStep > 0 && (
                                                            <Button size="large" onClick={prev} style={{ minWidth: '120px' }}>
                                                                ← Précédent
                                                            </Button>
                                                        )}
                                                        {currentStep < steps.length - 1 && (
                                                            <Button
                                                                type="primary"
                                                                size="large"
                                                                onClick={next}
                                                                style={{
                                                                    background: "#242166",
                                                                    border: 'none',
                                                                    minWidth: '120px'
                                                                }}
                                                            >
                                                                Suivant →
                                                            </Button>
                                                        )}
                                                        {currentStep === steps.length - 1 && (
                                                            <Button
                                                                type="primary"
                                                                size="large"
                                                                onClick={handleSubmit}
                                                                style={{
                                                                    background: "#242166",
                                                                    border: 'none',
                                                                    minWidth: '180px'
                                                                }}
                                                                icon={<CheckCircleOutlined />}
                                                                loading={loading}
                                                            >
                                                                Enregistrer la Déclaration
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </div>
                                            </Card>
                                        </Col>
                                        {/* </div> */}
                                    </div>
                                </ConfigProvider>

                            </div>
                        </div>
                        {/* <!-- / Content --> */}

                        {/* <!-- Footer --> */}
                        <Footer />
                        {/* <!-- / Footer --> */}

                        <div className="content-backdrop fade"></div>
                    </div>
                    {/* <!-- Content wrapper --> */}
                </div>
                {/* <!-- / Layout page --> */}
            </div>

            {/* <!-- Overlay --> */}
            <div className="layout-overlay layout-menu-toggle"></div>

            {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
            <div className="drag-target"></div>
        </div>
    );
};

export default DeclarationSinistreForm;
